import React, { useState, useEffect } from "react";
import { getDatabase, ref, onValue, off, set, remove } from "firebase/database";
import app from "../../../firebaseConfig";

export default function CirclesLayout() {
  const db = getDatabase(app);

  const DEFAULT_PRICE = 85000;

  const [grupos, setGrupos] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [elementos, setElementos] = useState([]);

  // modales / formularios
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // form filamentos
  const [fName, setFName] = useState("");
  const [fImg, setFImg] = useState("");
  const [fCantidad, setFCantidad] = useState("");

  // form implementos (add)
  const [impCategoria, setImpCategoria] = useState("");
  const [impElemento, setImpElemento] = useState("");
  const [impCantidad, setImpCantidad] = useState("");
  const [impPrecio, setImpPrecio] = useState("");

  // form delete selections
  const [delFilamento, setDelFilamento] = useState("");
  const [delImpCategoria, setDelImpCategoria] = useState("");
  const [delImpElemento, setDelImpElemento] = useState("");

  // Totales
  const [partialTotal, setPartialTotal] = useState(0); // total parcial para selectedCategory
  const [totalGlobal, setTotalGlobal] = useState(0); // suma de totales parciales de todos los grupos


  const [showEditModal, setShowEditModal] = useState(false);
const [selectedFilamento, setSelectedFilamento] = useState(null);
const [editCantidad, setEditCantidad] = useState(0);


  // ----------------
  // Cargar inventario completo (grupos + calcular total global)
  // ----------------
  useEffect(() => {
    const inventarioRef = ref(db, "nature/Awoo/Inventario");
    const listener = onValue(inventarioRef, (snapshot) => {
      const data = snapshot.val() || {};

      // build grupos list (with img) for UI
      const arr = Object.keys(data).map((nombre) => ({
        nombre,
        img: data[nombre]?.Img || "https://via.placeholder.com/100",
      }));
      setGrupos(arr);
      if (!selectedCategory && arr.length > 0) setSelectedCategory(arr[0].nombre);

      // compute global total by iterating every group's Elementos node
      let globalSum = 0;
      Object.keys(data).forEach((groupName) => {
        const groupNode = data[groupName];
        const elementosNode = groupNode?.Elementos;
        if (!elementosNode) return;

        // helper to compute total for this group
        const groupTotal = computeTotalFromElementosNode(elementosNode);
        globalSum += groupTotal;
      });

      setTotalGlobal(globalSum);
    });

    return () => {
      off(inventarioRef);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // se ejecuta una vez y escucha inventario completo

  // ----------------
  // Cargar elementos del group seleccionado (ya con lógica previa)
  // ----------------
  useEffect(() => {
    if (!selectedCategory) {
      setElementos([]);
      setPartialTotal(0);
      return;
    }

    const elementosRef = ref(db, `nature/Awoo/Inventario/${selectedCategory}/Elementos`);
    const listener = onValue(elementosRef, async (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setElementos([]);
        setPartialTotal(0);
        return;
      }

      const firstVal = Object.values(data)[0];

      // --- Caso: Estructura con 2 niveles (nodo1 -> nodo2 -> {Cantidad, Precio})
      if (
        firstVal &&
        typeof firstVal === "object" &&
        Object.values(firstVal)[0] &&
        typeof Object.values(firstVal)[0] === "object" &&
        ("Cantidad" in Object.values(firstVal)[0] || "Precio" in Object.values(firstVal)[0])
      ) {
        const arr = Object.keys(data).map((key1) => {
          const subObj = data[key1] || {};
          const subitems = Object.keys(subObj).map((key2) => ({
            nombre: key2,
            cantidad: subObj[key2].Cantidad ?? 0,
            precio: subObj[key2].Precio ?? 0,
          }));
          return {
            nombre: key1,
            subitems,
          };
        });
        setElementos(arr);
        // compute partial total
        const p = arr.reduce((acc, g) => {
          return acc + g.subitems.reduce((sacc, si) => sacc + (Number(si.cantidad || 0) * Number(si.precio || 0)), 0);
        }, 0);
        setPartialTotal(p);
        return;
      }

      // --- Caso: elementos con {Cantidad, Precio} directamente (1 nivel)
      if (firstVal && typeof firstVal === "object" && ("Cantidad" in firstVal || "Precio" in firstVal)) {
        const arr = Object.keys(data).map((key) => ({
          nombre: key,
          cantidad: Number(data[key].Cantidad ?? 0),
          precio: Number(data[key].Precio ?? 0),
          img: data[key].Img || "https://via.placeholder.com/80",
        }));
        setElementos(arr);

        // compute partial total for this category
        const p = arr.reduce((acc, i) => acc + (Number(i.cantidad || 0) * Number(i.precio || 0)), 0);
        setPartialTotal(p);
        return;
      }

      // --- Caso: Filamentos (Nombre, Img, Cantidad, Precio opcional)
      if (firstVal && typeof firstVal === "object" && (firstVal.Nombre || firstVal.Img || firstVal.Cantidad || firstVal.Precio)) {
        // Build array, ensure Precio exists and >=1, otherwise write default to Firebase
        const arr = await Promise.all(Object.keys(data).map(async (key) => {
          const node = data[key] || {};
          const nombre = node.Nombre || key;
          const img = node.Img || "https://via.placeholder.com/80";
          const cantidad = Number(node.Cantidad ?? 0);
          let precio = node.Precio !== undefined && node.Precio !== null ? Number(node.Precio) : undefined;

          // si no tiene precio o <= 0 => asignar DEFAULT_PRICE y guardar en Firebase
          if (precio === undefined || precio <= 0) {
            precio = DEFAULT_PRICE;
            try {
              // escribir solo el campo Precio
              await set(ref(db, `nature/Awoo/Inventario/Filamentos/Elementos/${key}/Precio`), precio);
            } catch (err) {
              console.error("Error guardando precio por defecto:", err);
            }
          }

          return { nombre, img, cantidad, precio };
        }));

        setElementos(arr);

        // compute partial total for filaments
        const p = arr.reduce((acc, i) => acc + (Number(i.cantidad || 0) * Number(i.precio || 0)), 0);
        setPartialTotal(p);
        return;
      }

      // --- Caso: simple clave => número (no price) -> tratamos cantidad sin precio
      const arrSimple = Object.keys(data).map((key) => ({
        nombre: key,
        cantidad: Number(data[key]),
      }));
      setElementos(arrSimple);
      setPartialTotal(0);
    });

    return () => off(elementosRef);
  }, [selectedCategory, db]);

  // ----------------
  // Helpers
  // ----------------

  // Calcula total (suma Cantidad*Precio) dado un nodo Elementos (objeto JS desde snapshot)
  function computeTotalFromElementosNode(elementosNode) {
    if (!elementosNode || typeof elementosNode !== "object") return 0;
    const firstVal = Object.values(elementosNode)[0];

    // caso 2 niveles: nodo1 -> nodo2 -> {Cantidad, Precio}
    if (
      firstVal &&
      typeof firstVal === "object" &&
      Object.values(firstVal)[0] &&
      typeof Object.values(firstVal)[0] === "object" &&
      ("Cantidad" in Object.values(firstVal)[0] || "Precio" in Object.values(firstVal)[0])
    ) {
      // iterate each nodo1
      return Object.keys(elementosNode).reduce((gAcc, key1) => {
        const subObj = elementosNode[key1] || {};
        return gAcc + Object.keys(subObj).reduce((sAcc, key2) => {
          const cant = Number(subObj[key2].Cantidad ?? 0);
          const prec = Number(subObj[key2].Precio ?? 0);
          return sAcc + (cant * prec);
        }, 0);
      }, 0);
    }

    // caso 1 nivel con Cantidad & Precio
    if (firstVal && typeof firstVal === "object" && ("Cantidad" in firstVal || "Precio" in firstVal)) {
      return Object.keys(elementosNode).reduce((acc, key) => {
        const node = elementosNode[key] || {};
        const cant = Number(node.Cantidad ?? 0);
        const prec = Number(node.Precio ?? 0);
        return acc + (cant * prec);
      }, 0);
    }

    // caso Filamentos-like where nodes have Nombre, Img, Cantidad, Precio maybe
    if (firstVal && typeof firstVal === "object" && (firstVal.Nombre || firstVal.Img || firstVal.Cantidad || firstVal.Precio)) {
      return Object.keys(elementosNode).reduce((acc, key) => {
        const node = elementosNode[key] || {};
        const cant = Number(node.Cantidad ?? 0);
        let prec = Number(node.Precio ?? 0);
        if (!prec || prec <= 0) prec = DEFAULT_PRICE; // assume default if missing (but we also write elsewhere)
        return acc + (cant * prec);
      }, 0);
    }

    // else: can't compute price (simple numbers)
    return 0;
  }

  // Acciones: agregar / eliminar elementos en Firebase
  const handleAddElement = async () => {
    try {
      if (selectedCategory === "Filamentos") {
        if (!fName) return alert("Ingrese Nombre para el filamento.");
        const path = `nature/Awoo/Inventario/Filamentos/Elementos/${fName}`;
        await set(ref(db, path), {
          Nombre: fName,
          Img: fImg || "",
          Cantidad: Number(fCantidad) || 0,
          Precio: Number(DEFAULT_PRICE) // al crear filamento nuevo asignamos default precio (o podrías pedir precio también)
        });
        alert("Filamento agregado/actualizado correctamente.");
        setFName(""); setFImg(""); setFCantidad("");
        setShowAddModal(false);
      } else {
        if (!impCategoria || !impElemento) return alert("Ingrese Categoria y Elemento.");
        const path = `nature/Awoo/Inventario/${selectedCategory}/Elementos/${impCategoria}/${impElemento}`;
        await set(ref(db, path), {
          Cantidad: Number(impCantidad) || 0,
          Precio: Number(impPrecio) || 0,
        });
        alert("Implemento agregado/actualizado correctamente.");
        setImpCategoria(""); setImpElemento(""); setImpCantidad(""); setImpPrecio("");
        setShowAddModal(false);
      }
    } catch (err) {
      console.error(err);
      alert("Error al guardar en Firebase: " + (err.message || err));
    }
  };

  const handleDeleteElement = async () => {
    try {
      if (selectedCategory === "Filamentos") {
        if (!delFilamento) return alert("Seleccione un filamento para eliminar.");
        const path = `nature/Awoo/Inventario/Filamentos/Elementos/${delFilamento}`;
        if (!window.confirm(`Eliminar filamento "${delFilamento}"?`)) return;
        await remove(ref(db, path));
        alert("Filamento eliminado.");
        setDelFilamento("");
        setShowDeleteModal(false);
      } else {
        if (!delImpCategoria || !delImpElemento) return alert("Seleccione categoría y elemento a eliminar.");
        const path = `nature/Awoo/Inventario/${selectedCategory}/Elementos/${delImpCategoria}/${delImpElemento}`;
        if (!window.confirm(`Eliminar ${delImpElemento} en ${delImpCategoria}?`)) return;
        await remove(ref(db, path));
        alert("Implemento eliminado.");
        setDelImpCategoria(""); setDelImpElemento("");
        setShowDeleteModal(false);
      }
    } catch (err) {
      console.error(err);
      alert("Error al eliminar en Firebase: " + (err.message || err));
    }
  };

  // Helpers para selects
  const filamentosNombres = elementos && elementos.length > 0 && elementos[0].img !== undefined
    ? elementos.map((e) => e.nombre)
    : [];

  const implCategorias = elementos && elementos.length > 0 && elementos[0].subitems
    ? elementos.map((e) => e.nombre)
    : [];

  const implElementosForCategoria = (categoria) => {
    const found = elementos.find((e) => e.nombre === categoria);
    if (!found || !found.subitems) return [];
    return found.subitems.map((s) => s.nombre);
  };

  const formatPrecio = (p) => (p === undefined || p === null ? "-" : `$${Number(p).toLocaleString("es-CO")}`);
  const formatNumber = (n) => Number(n || 0).toLocaleString("es-CO");

  // ===========================
  // RENDER
  // ===========================
  return (
    <div style={{ backgroundColor: "black", color: "white", minHeight: "100vh", padding: "20px" }}>
      {/* header: Grupos title + Total Global top-right */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
        <h2 style={{ margin: 0 }}>Grupos</h2>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "12px", color: "#ccc" }}>Total Global</div>
          <div style={{ fontWeight: "700", fontSize: "18px" }}>{formatNumber(totalGlobal)}</div>
        </div>
      </div>

      {/* Scroll horizontal de grupos */}
      <div style={{ display: "flex", overflowX: "auto", justifyContent: "center", gap: "40px", paddingBottom: "20px", scrollSnapType: "x mandatory" }}>
        {grupos.map((grupo, index) => (
          <div key={index} onClick={() => setSelectedCategory(grupo.nombre)} style={{ textAlign: "center", cursor: "pointer" }}>
            <img src={grupo.img} alt={grupo.nombre} style={{ width: "100px", height: "100px", borderRadius: "50%", objectFit: "cover", border: selectedCategory === grupo.nombre ? "3px solid cyan" : "2px solid white", transition: "all 0.3s" }} />
            <p style={{ marginTop: "8px", fontWeight: "bold" }}>{grupo.nombre}</p>
          </div>
        ))}
      </div>

      {/* Elementos controls + Partial total display */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
        <h2 style={{ margin: 0 }}>Elementos</h2>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "12px", color: "#ccc" }}>Total Parcial</div>
            <div style={{ fontWeight: "700", fontSize: "16px" }}>{formatNumber(partialTotal)}</div>
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <button onClick={() => setShowAddModal(true)} style={botonAccion}>➕</button>
            <button onClick={() => setShowDeleteModal(true)} style={botonAccion}>🗑️</button>
          </div>
        </div>
      </div>

{/* Grid de Filamentos EXACTO si selectedCategory === "Filamentos" */}
{selectedCategory === "Filamentos" ? (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
      gap: "20px",
      justifyItems: "center",
      margin: "0 auto",
      width: "85%",
    }}
  >
    {elementos.map((item, index) => (
      <div
        key={index}
        style={{ textAlign: "center", position: "relative" }}
        onClick={() => {
          setSelectedFilamento(item);
          setEditCantidad(item.cantidad);
          setShowEditModal(true);
        }}
      >
        {/* Imagen con overlay si cantidad = 0 */}
        <div style={{ position: "relative" }}>
          <img
            src={item.img || "https://via.placeholder.com/80"}
            alt={item.nombre}
            style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              objectFit: "cover",
              border: "2px solid white",
              opacity: item.cantidad === 0 ? 0.5 : 1,
              filter: item.cantidad === 0 ? "grayscale(90%)" : "none",
            }}
          />
          {/* capa gris + línea roja */}
          {item.cantidad === 0 && (
            <>
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(80,80,80,0.5)",
                  borderRadius: "50%",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "5%",
                  width: "90%",
                  height: "3px",
                  backgroundColor: "red",
                  transform: "rotate(-25deg)",
                  borderRadius: "3px",
                }}
              />
            </>
          )}
        </div>

        <p style={{ marginTop: "6px", fontSize: "14px", fontWeight: "500" }}>
          {item.nombre}
        </p>
        <p style={{ marginTop: "2px", fontSize: "13px", color: "#ccc" }}>
          {item.cantidad}
        </p>
        <div style={{ marginTop: "6px", color: "#fff", fontWeight: 600 }}>
          💰 {formatPrecio(item.precio)}
        </div>
      </div>
    ))}
  </div>
) : (
        // Vista para Implementos u otras categorías (estructura anidada)
        <div style={{ display: "flex", flexDirection: "column", gap: "15px", alignItems: "center", marginTop: "20px" }}>
          {elementos.length === 0 && <div style={{ color: "#888" }}>No hay elementos</div>}
          {elementos.map((item, index) => (
            <div key={index} style={{ backgroundColor: "#111", padding: "14px 18px", borderRadius: "12px", border: "1px solid white", width: "85%", textAlign: "left" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <strong style={{ color: "white", fontSize: "16px" }}>{item.nombre}</strong>
                {item.cantidad !== undefined && <span style={{ color: "white", fontSize: "14px" }}>{item.cantidad}</span>}
              </div>
              {item.subitems && item.subitems.length > 0 && (
  <div style={{ marginTop: "10px", display: "flex", flexDirection: "column", gap: "6px" }}>
{item.subitems.map((s, si) => {
  const totalItem = Number(s.cantidad || 0) * Number(s.precio || 0);
  return (
    <div
      key={si}
      onClick={() => {
        setSelectedFilamento({ ...s, categoria: item.nombre });
        setEditCantidad(s.cantidad);
        setShowEditModal(true);
      }}
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: "12px",
        alignItems: "center",
        borderBottom: "1px solid #333",
        paddingBottom: "4px",
        cursor: "pointer",
      }}
    >
      <span style={{ color: "#ddd" }}>{s.nombre}</span>
      <span style={{ color: "white" }}>
        🧮 {s.cantidad} &nbsp; | &nbsp; 💰 {formatPrecio(s.precio)} &nbsp; | &nbsp; 💲
        {formatNumber(totalItem)}
      </span>
    </div>
  );
})}

  </div>
)}


              {!item.subitems && item.precio !== undefined && <div style={{ marginTop: "10px", color: "#ccc" }}>🧮 {item.cantidad} &nbsp; | &nbsp; 💰 {formatPrecio(item.precio)}</div>}
            </div>
          ))}
        </div>
      )}



      {/* -----------------
          MODAL AGREGAR
         ----------------- */}
      {showAddModal && (
        <div style={modalOverlay}>
          <div style={modalBox}>
            <h3 style={{ marginTop: 0 }}>Agregar elemento ({selectedCategory})</h3>

            {selectedCategory === "Filamentos" ? (
              <>
                <label style={label}>Nombre</label>
                <input style={input} value={fName} onChange={(e) => setFName(e.target.value)} />
                <label style={label}>Img (URL)</label>
                <input style={input} value={fImg} onChange={(e) => setFImg(e.target.value)} />
                <label style={label}>Cantidad</label>
                <input style={input} value={fCantidad} onChange={(e) => setFCantidad(e.target.value)} type="number" />
              </>
            ) : (
              <>
                <label style={label}>Categoria</label>
                <input style={input} value={impCategoria} onChange={(e) => setImpCategoria(e.target.value)} placeholder="p.ej. Cargadores" />
                <label style={label}>Elemento</label>
                <input style={input} value={impElemento} onChange={(e) => setImpElemento(e.target.value)} placeholder="p.ej. Cargador USB" />
                <label style={label}>Cantidad</label>
                <input style={input} value={impCantidad} onChange={(e) => setImpCantidad(e.target.value)} type="number" />
                <label style={label}>Precio</label>
                <input style={input} value={impPrecio} onChange={(e) => setImpPrecio(e.target.value)} type="number" />
              </>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "12px" }}>
              <button onClick={() => setShowAddModal(false)} style={modalBtnSecondary}>Cancelar</button>
              <button onClick={handleAddElement} style={modalBtnPrimary}>Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* -----------------
          MODAL ELIMINAR
         ----------------- */}
      {showDeleteModal && (
        <div style={modalOverlay}>
          <div style={modalBox}>
            <h3 style={{ marginTop: 0 }}>Eliminar elemento ({selectedCategory})</h3>

            {selectedCategory === "Filamentos" ? (
              <>
                <label style={label}>Selecciona filamento</label>
                <select style={input} value={delFilamento} onChange={(e) => setDelFilamento(e.target.value)}>
                  <option value="">-- elegir --</option>
                  {filamentosNombres.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </>
            ) : (
              <>
                <label style={label}>Categoria</label>
                <select style={input} value={delImpCategoria} onChange={(e) => { setDelImpCategoria(e.target.value); setDelImpElemento(""); }}>
                  <option value="">-- elegir categoria --</option>
                  {implCategorias.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>

                <label style={label}>Elemento</label>
                <select style={input} value={delImpElemento} onChange={(e) => setDelImpElemento(e.target.value)}>
                  <option value="">-- elegir elemento --</option>
                  {delImpCategoria && implElementosForCategoria(delImpCategoria).map((el) => <option key={el} value={el}>{el}</option>)}
                </select>
              </>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "12px" }}>
              <button onClick={() => setShowDeleteModal(false)} style={modalBtnSecondary}>Cancelar</button>
              <button onClick={handleDeleteElement} style={modalBtnPrimary}>Eliminar</button>
            </div>
          </div>
        </div>
      )}


{/* -----------------
    MODAL EDITAR CANTIDAD
   ----------------- */}
{showEditModal && selectedFilamento && (
  <div style={modalOverlay}>
    <div style={modalBox}>
      <h3 style={{ marginTop: 0 }}>Editar cantidad</h3>
      <p>
        <strong style={{ color: "cyan" }}>{selectedFilamento.nombre}</strong>
      </p>
      <div style={{ display: "flex", justifyContent: "center", gap: "15px", alignItems: "center", margin: "15px 0" }}>
        <button
          style={modalBtnSecondary}
          onClick={() => setEditCantidad((c) => Math.max(0, c - 1))}
        >
          ➖
        </button>
        <span style={{ fontSize: "20px" }}>{editCantidad}</span>
        <button
          style={modalBtnSecondary}
          onClick={() => setEditCantidad((c) => c + 1)}
        >
          ➕
        </button>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
        <button
          style={modalBtnSecondary}
          onClick={() => setShowEditModal(false)}
        >
          Cancelar
        </button>
        <button
          style={modalBtnPrimary}
          onClick={async () => {
            try {
              await set(
                ref(
                  db,
                  `nature/Awoo/Inventario/Filamentos/Elementos/${selectedFilamento.nombre}/Cantidad`
                ),
                Number(editCantidad)
              );
              setShowEditModal(false);
            } catch (err) {
              console.error(err);
              alert("Error al guardar: " + err.message);
            }
          }}
        >
          Modificar
        </button>
      </div>
    </div>
  </div>
)}


{/* -----------------
    MODAL EDITAR CANTIDAD IMPLEMENTOS
   ----------------- */}
{showEditModal && selectedCategory !== "Filamentos" && selectedFilamento && (
  <div style={modalOverlay}>
    <div style={modalBox}>
      <h3 style={{ marginTop: 0 }}>Editar cantidad</h3>
      <p>
        <strong style={{ color: "cyan" }}>{selectedFilamento.nombre}</strong>
      </p>
      <div style={{ display: "flex", justifyContent: "center", gap: "15px", alignItems: "center", margin: "15px 0" }}>
        <button
          style={modalBtnSecondary}
          onClick={() => setEditCantidad((c) => Math.max(0, c - 1))}
        >
          ➖
        </button>
        <span style={{ fontSize: "20px" }}>{editCantidad}</span>
        <button
          style={modalBtnSecondary}
          onClick={() => setEditCantidad((c) => c + 1)}
        >
          ➕
        </button>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
        <button
          style={modalBtnSecondary}
          onClick={() => setShowEditModal(false)}
        >
          Cancelar
        </button>
        <button
          style={modalBtnPrimary}
          onClick={async () => {
            try {
              await set(
                ref(
                  db,
                  `nature/Awoo/Inventario/${selectedCategory}/Elementos/${selectedFilamento.categoria}/${selectedFilamento.nombre}/Cantidad`
                ),
                Number(editCantidad)
              );
              setShowEditModal(false);
            } catch (err) {
              console.error(err);
              alert("Error al actualizar cantidad: " + err.message);
            }
          }}
        >
          Guardar
        </button>
      </div>
    </div>
  </div>
)}




    </div>
  );
}

// estilos reutilizables
const botonAccion = {
  backgroundColor: "cyan",
  color: "black",
  border: "none",
  borderRadius: "50%",
  width: "35px",
  height: "35px",
  fontSize: "18px",
  cursor: "pointer",
  fontWeight: "bold",
};

// modal styles
const modalOverlay = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.6)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 9999,
};

const modalBox = {
  backgroundColor: "#111",
  color: "white",
  padding: "18px",
  borderRadius: "10px",
  width: "420px",
  maxWidth: "92%",
  boxShadow: "0 6px 20px rgba(0,0,0,0.6)",
  border: "1px solid #333",
};

const label = { fontSize: "13px", marginTop: "8px", color: "#ddd", display: "block" };
const input = { width: "100%", padding: "8px 10px", marginTop: "6px", borderRadius: "6px", border: "1px solid #333", background: "#222", color: "white" };

const modalBtnPrimary = { background: "cyan", color: "black", border: "none", padding: "8px 12px", borderRadius: "8px", cursor: "pointer" };
const modalBtnSecondary = { background: "#333", color: "white", border: "none", padding: "8px 12px", borderRadius: "8px", cursor: "pointer" };
